import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { translateAuthError } from '../lib/authErrors';
import { supabase } from '../lib/supabase';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

interface Profile {
  full_name: string | null;
  organization: string | null;
}

const EMBER_BTN =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-ember px-5 py-2.5 font-semibold text-ink-950 transition-all duration-200 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed';
const ERR_BOX = 'rounded-xl bg-risk-high/10 border border-risk-high/30 text-risk-high text-sm p-3';
const OK_BOX =
  'rounded-xl bg-tutela-growth/10 border border-tutela-growth/30 text-tutela-growth text-sm p-3';

const MIN_LEN = 8;

export default function Conta() {
  useDocumentMeta('A minha conta | Tutela', undefined, undefined, { noindex: true });
  const { user, signOut, signInWithPassword, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSaved, setPwSaved] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('full_name, organization')
      .eq('id', user.id)
      .single()
      .then(({ data, error }) => {
        if (error) setError('Não foi possível carregar o perfil.');
        else if (data) {
          setProfile(data);
          setFullName(data.full_name ?? '');
        }
        setLoading(false);
      });
  }, [user]);

  async function save() {
    if (!user) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    const { error } = await supabase
      .from('profiles')
      .update({ full_name: fullName.trim() || null })
      .eq('id', user.id);
    setSaving(false);
    if (error) {
      setError('Não foi possível guardar as alterações.');
      return;
    }
    setSaved(true);
  }

  /**
   * Change password while logged in. We re-authenticate with the current
   * password first: `updateUser` alone would let anyone holding a live session
   * (a borrowed/unlocked machine) silently take over the account.
   */
  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setPwError(null);
    setPwSaved(false);
    if (newPw.length < MIN_LEN) {
      setPwError(`A nova palavra-passe deve ter pelo menos ${MIN_LEN} caracteres.`);
      return;
    }
    if (newPw !== confirmPw) {
      setPwError('As palavras-passe não coincidem.');
      return;
    }
    if (newPw === currentPw) {
      setPwError('A nova palavra-passe tem de ser diferente da atual.');
      return;
    }
    if (!user?.email) return;
    setPwSaving(true);
    const reauth = await signInWithPassword(user.email, currentPw);
    if (reauth.error) {
      setPwSaving(false);
      setPwError('A palavra-passe atual está incorreta.');
      return;
    }
    const { error } = await updatePassword(newPw);
    setPwSaving(false);
    if (error) {
      setPwError(translateAuthError(error));
      return;
    }
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setPwSaved(true);
  }

  async function onSignOut() {
    await signOut();
    navigate('/', { replace: true });
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-display text-2xl font-bold text-mist mb-1">A minha conta</h1>
        <p className="text-sm text-mist-muted mb-8">
          Área pessoal reservada a organizações aprovadas.
        </p>

        <div className="bg-ink-800 border border-ink-700 rounded-2xl p-6 sm:p-8 space-y-6">
          {loading ? (
            <p className="text-mist-muted text-sm">A carregar...</p>
          ) : (
            <>
              <div>
                <span className="label-dark">Email</span>
                <p className="text-mist">{user?.email}</p>
              </div>
              <div>
                <span className="label-dark">Organização</span>
                <p className="text-mist">{profile?.organization ?? 'Não definida'}</p>
              </div>
              <div>
                <label className="label-dark" htmlFor="fullName">
                  Nome
                </label>
                <input
                  id="fullName"
                  className="input-dark"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    setSaved(false);
                  }}
                  placeholder="O seu nome"
                />
              </div>
              {error && <p className={ERR_BOX}>{error}</p>}
              {saved && <p className={OK_BOX}>Alterações guardadas.</p>}
              <div className="flex items-center justify-between pt-2">
                <button type="button" onClick={save} disabled={saving} className={EMBER_BTN}>
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
                <button
                  type="button"
                  onClick={onSignOut}
                  className="text-sm text-mist-muted hover:text-mist transition-colors"
                >
                  Terminar sessão
                </button>
              </div>
            </>
          )}
        </div>

        {!loading && (
          <div className="mt-6 bg-ink-800 border border-ink-700 rounded-2xl p-6 sm:p-8">
            <h2 className="font-display text-lg font-semibold text-mist mb-1">Palavra-passe</h2>
            <p className="text-sm text-mist-muted mb-6">
              Altere a sua palavra-passe. Pedimos a atual para confirmar que é mesmo o titular da
              conta.
            </p>
            <form onSubmit={changePassword} className="space-y-4">
              <div>
                <label className="label-dark" htmlFor="currentPw">
                  Palavra-passe atual
                </label>
                <input
                  id="currentPw"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="input-dark"
                  value={currentPw}
                  onChange={(e) => {
                    setCurrentPw(e.target.value);
                    setPwSaved(false);
                  }}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="label-dark" htmlFor="newPw">
                  Nova palavra-passe
                </label>
                <input
                  id="newPw"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_LEN}
                  className="input-dark"
                  value={newPw}
                  onChange={(e) => {
                    setNewPw(e.target.value);
                    setPwSaved(false);
                  }}
                  placeholder="••••••••"
                />
                <p className="mt-1 text-xs text-mist-muted">Mínimo {MIN_LEN} caracteres.</p>
              </div>
              <div>
                <label className="label-dark" htmlFor="confirmPw">
                  Confirmar nova palavra-passe
                </label>
                <input
                  id="confirmPw"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_LEN}
                  className="input-dark"
                  value={confirmPw}
                  onChange={(e) => {
                    setConfirmPw(e.target.value);
                    setPwSaved(false);
                  }}
                  placeholder="••••••••"
                />
              </div>
              {pwError && <p className={ERR_BOX}>{pwError}</p>}
              {pwSaved && <p className={OK_BOX}>Palavra-passe alterada.</p>}
              <button type="submit" disabled={pwSaving} className={EMBER_BTN}>
                {pwSaving ? 'A alterar...' : 'Alterar palavra-passe'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
